/**
 * THE CHAT LIST'S ONE ORDER (ITP-E-240-04, Plan ITP-P-48 story 7). The pinned chats lead, in the order they are given: the
 * server's, the newest pin first (B-05). Below them, the chats newest first by their latest message: the date the row shows
 * as its time (`lastMessage.createdDate`), or, for a chat with nothing in it yet, the server's key for it
 * (`lastMessageReceived`: when the chat was created). The archived chats come last, ordered the same way (B-07). Ties keep
 * the order given.
 *
 * "Latest message" is any row of the thread: a text message, a message with files, a card row, a call row, a system line.
 * A read, a mark, a pin, a mute or an archive is not activity: none of them changes a row's date. The server orders and
 * pages by the same key (`Conversation.LastMessageDate`), so a reload gives this order on every page. The list on the
 * Messages page and in the Chat panel, and the store that the forward picker reads, all order through this function: there
 * is no second sort.
 */

/** The milliseconds of an ISO date, or null when there is none or it cannot be read. */
function timeOf(value: string | Date | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
}

/**
 * When a row's chat was last active, in milliseconds: its latest message's date, the date its time shows; with no message,
 * the server's key for it (`lastMessageReceived`); 0 when the row says nothing.
 */
export function chatActivity(conversation: any): number {
  return timeOf(conversation?.lastMessage?.createdDate) ?? timeOf(conversation?.lastMessageReceived) ?? 0;
}

/** The server's word on a row's own marks: pinned (B-05) and archived (B-07). */
export const isPinnedRow = (conversation: any): boolean => !!conversation?.isPinned;
export const isArchivedRow = (conversation: any): boolean => !!conversation?.isArchived;

/**
 * `conversations` in the list's order: the pinned first in the order given, then the others and last the archived, each
 * newest first by `chatActivity`. The same array when nothing moves, so a store that keeps it changes nothing.
 */
export function orderChatList<T>(conversations: T[], pinned: (conversation: T) => boolean = isPinnedRow,
                                 archived: (conversation: T) => boolean = isArchivedRow): T[] {
  if (!conversations || conversations.length < 2) {
    return conversations;
  }
  const ordered = conversations
    .map((conversation, index) => ({
      conversation,
      index,
      rank: archived(conversation) ? 2 : pinned(conversation) ? 0 : 1,
      at: chatActivity(conversation)
    }))
    .sort((a, b) => a.rank - b.rank || (a.rank === 0 ? 0 : b.at - a.at) || a.index - b.index)
    .map(entry => entry.conversation);
  return ordered.every((conversation, index) => conversation === conversations[index]) ? conversations : ordered;
}

/** Whether the loaded list holds the conversation: a message for one it does not hold has to bring it in. */
export function listHolds(conversations: any[] | null | undefined, conversationId: string): boolean {
  return !!conversationId && (conversations || []).some(conversation => conversation?.id === conversationId);
}
